<template>
  <div v-show="visible && article" class="tooltip-box" :class="position" :style="tooltipStyle">
    <div class="tooltip-header">
      <a href="javascript:void(0);" class="ref-title-link" @click="openArticleInRightPanel(article)">
        <div class="ref-title">{{ article?.title || "暂无标题" }}</div>
      </a>
      <div class="ref-title-zh" v-if="article?.title_zh">{{ article.title_zh }}</div>
    </div>

    <div class="tooltip-content">
      <div class="metric-row">
        <span class="metric-pill score-pill" v-if="getScore(article) !== null">评分 {{ formatScore(getScore(article)) }}</span>
        <span class="metric-pill" v-if="getPublishTimeLabel(article)">发布时间 {{ getPublishTimeLabel(article) }}</span>
        <span class="metric-pill" v-if="getCitationCount(article) !== null">引用 {{ formatInteger(getCitationCount(article)) }}</span>
      </div>

      <div class="meta-info">
        <span v-if="article?.source"><i class="el-icon-document"></i>{{ article.source }}</span>
        <span v-if="article?.year || article?.pubdate"><i class="el-icon-date"></i>{{ article.year || article.pubdate }}</span>
      </div>

      <div class="doi-info" v-if="article?.doi">DOI {{ cleanDoi(article.doi) }}</div>

      <div class="author-info" v-if="article?.authors && article.authors.length" :title="formatAuthors(article.authors)">
        <span>作者：</span>{{ formatAuthors(article.authors) }}
      </div>

      <div class="description-wrap" v-if="article?.abstract">
        <div class="description-label">摘要</div>
        <div class="description-content" :title="article.abstract">{{ article.abstract }}</div>
      </div>
      <div class="description-wrap zh" v-if="article?.abstract_zh">
        <div class="description-label">中文摘要</div>
        <div class="description-content" :title="article.abstract_zh">{{ article.abstract_zh }}</div>
      </div>
    </div>

    <div class="tooltip-arrow"></div>
  </div>
</template>

<script>
import { openArticleDetail } from "../utils/openArticleDetail";
import { cleanDoi } from "@/utils/doiUtils";
export default {
  name: "ArticleTooltip",
  props: {
    visible: {
      type: Boolean,
      default: false,
      required: true,
    },
    article: {
      type: [Object, null],
      default: null,
      validator: value => {
        if (value === null) return true;
        return typeof value === "object";
      },
    },
    position: {
      type: String,
      default: "right",
      validator: val => ["top", "bottom", "left", "right"].includes(val),
    },
    coordinates: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
      required: true,
    },
  },
  data() {
    return {
      // 移除折叠相关变量
    };
  },
  methods: {
    cleanDoi(doi) {
      return cleanDoi(doi);
    },
    full_txt_art_analysis(sid, title, is_create_task) {
      this.$emit("full_txt_art_analysis", sid, title, is_create_task);
    },
    formatAuthors(authors) {
      // 后端已在 ES 边界将作者归一化为字符串数组，前端直接消费字符串。
      if (!Array.isArray(authors)) return "未知作者";
      const authorText = authors.filter(author => typeof author === "string" && author).join(", ");
      return authorText || "未知作者";
    },
    formatScore(score) {
      const numberScore = Number(score);
      if (Number.isNaN(numberScore)) return score;
      return numberScore.toFixed(2);
    },
    formatInteger(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return value;
      return Math.round(numberValue).toLocaleString();
    },
    getScore(article) {
      if (!article) return null;
      // 后端规范字段：ResearchSearchResult.score。严格单字段。
      const value = article.score;
      if (value === undefined || value === null || value === "") return null;
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },
    getCitationCount(article) {
      if (!article) return null;
      // 后端规范字段：ResearchSearchResult.times_cited。严格单字段。
      const value = article.times_cited;
      if (value === undefined || value === null || value === "") return null;
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },
    getPublishTimeLabel(article) {
      if (!article) return "";
      // 后端规范字段：ResearchSearchResult.pubdate。严格单字段。
      return article.pubdate || "";
    },
    openArticleInRightPanel(article) {
      openArticleDetail(article);
    },
  },
  watch: {
    // 移除折叠相关监听
  },
  computed: {
    tooltipStyle() {
      const transformMap = {
        left: "translateY(-50%)",
        right: "translateY(-50%)",
        top: "translate(-50%, -100%)",
        bottom: "translateX(-50%)",
      };
      return {
        left: `${this.coordinates.x}px`,
        top: `${this.coordinates.y}px`,
        transform: transformMap[this.position] || "translateY(-50%)",
      };
    },
  },
};
</script>

<style scoped>
.tooltip-box {
  position: fixed;
  width: 360px;
  background: var(--bg-front, #ffffff);
  border-radius: 10px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
  padding: 14px 16px 12px;
  z-index: 3000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.16s ease, visibility 0.16s ease;
  border: 1px solid #dbe7f7;
  box-sizing: border-box;
  max-height: min(420px, calc(100vh - 24px));
  overflow: visible;
}

.tooltip-box:not([style*="display: none"]) {
  opacity: 1;
  visibility: visible;
}

.tooltip-header {
  padding-bottom: 9px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e6edf7;
}
.tooltip-content {
  font-size: 13px;
  color: var(--txt-color, #374151);
  line-height: 1.55;
}
.metric-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.metric-pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  color: #406086;
  background: #f3f7ff;
  font-size: 12px;
  line-height: 22px;
}
.score-pill {
  color: #175cd3;
  background: #eef5ff;
  font-weight: 700;
}
.meta-info {
  color: #6b7890;
  font-size: 12px;
  margin-bottom: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.meta-info span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.author-info {
  margin: 8px 0;
  color: #344054;
  font-size: 12px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.author-info span {
  color: #6b7890;
}
.doi-info {
  font-size: 12px;
  color: #667085;
  margin-bottom: 6px;
  word-break: break-all;
}
.ref-title-link {
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  text-decoration-color: rgba(37, 99, 235, 0.38);
  display: block;
  transition: color 0.2s ease;
}
.ref-title {
  margin: 0;
  font-size: 16px;
  color: #246bfe;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 700;
  word-break: break-word;
}
.ref-title-link:hover .ref-title {
  color: #0b55c8;
}
.ref-title-zh {
  margin-top: 6px;
  color: #1f2a44;
  font-size: 13px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.description-wrap {
  margin-top: 8px;
  padding: 8px 10px;
  background-color: #f7faff;
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  min-height: 0;
}
.description-wrap.zh {
  background: #f8fafc;
  border-left-color: #9fb7d9;
}
.description-label {
  margin-bottom: 4px;
  color: #45607f;
  font-size: 12px;
  font-weight: 700;
}
.description-content {
  color: #1f2937;
  font-size: 12px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 7px solid transparent;
}
.tooltip-box.right .tooltip-arrow {
  left: -14px;
  top: 50%;
  margin-top: -7px;
  border-right-color: #ffffff;
}
.tooltip-box.left .tooltip-arrow {
  right: -14px;
  top: 50%;
  margin-top: -7px;
  border-left-color: #ffffff;
}
.tooltip-box.bottom .tooltip-arrow {
  top: -14px;
  left: 50%;
  margin-left: -7px;
  border-bottom-color: #ffffff;
}
.tooltip-box.top .tooltip-arrow {
  bottom: -14px;
  left: 50%;
  margin-left: -7px;
  border-top-color: #ffffff;
}
@media (max-width: 640px) {
  .tooltip-box {
    width: 300px;
  }
}
</style>
